import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";
import { MaskLines } from "../components/Motion";
import { useAuth } from "../context/AuthContext";
import { errorText } from "../lib/api";
import MyLibrary from "../components/MyLibrary";
import SocialButtons from "../components/SocialButtons";

function AuthForm() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("register");
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const nav = useNavigate();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "register") await register(f.name, f.email, f.password); else await login(f.email, f.password);
      toast.success(mode === "register" ? "Account created" : "Welcome back");
      const next = params.get("next");
      if (next && next.startsWith("/")) nav(next);
    } catch (err) { toast.error(errorText(err)); }
    setBusy(false);
  };
  return (
    <div className="max-w-md">
      <div className="flex gap-6 border-b rule mb-8">
        {[["register", "Create account"], ["login", "Sign in"]].map(([k, l]) => (
          <button key={k} onClick={() => setMode(k)} data-testid={`account-tab-${k}`} className={`pb-3 -mb-px border-b-2 font-medium transition-colors ${mode === k ? "border-[var(--red)] text-[var(--ink)]" : "border-transparent text-[var(--muted)]"}`}>{l}</button>
        ))}
      </div>
      <div className="mb-8"><SocialButtons next={params.get("next")} /></div>
      <form onSubmit={submit} className="space-y-7" data-testid="account-form">
        {mode === "register" && <label className="block"><span className="eyebrow">Full name</span><input required value={f.name} onChange={set("name")} className="field" data-testid="account-name-input" /></label>}
        <label className="block"><span className="eyebrow">Email</span><input required type="email" value={f.email} onChange={set("email")} className="field" data-testid="account-email-input" /></label>
        <label className="block"><span className="eyebrow">Password {mode === "register" && "(min. 8 characters)"}</span><input required type="password" minLength={mode === "register" ? 8 : 1} value={f.password} onChange={set("password")} className="field" data-testid="account-password-input" /></label>
        <button disabled={busy} className="btn-red h-12 w-full font-semibold disabled:opacity-50" data-testid="account-submit-btn">{busy ? "Please wait…" : mode === "register" ? "Create account" : "Sign in"}</button>
      </form>
    </div>
  );
}

function Profile() {
  const { user, logout } = useAuth();
  const sub = user?.subscription;
  return (
    <div className="max-w-xl" data-testid="account-profile">
      <p className="f-serif text-3xl">{user?.name || user?.email}</p>
      <p className="text-[var(--muted)] mt-1">{user?.email}</p>
      <div className="mt-10 border border-[var(--ink)] p-7">
        <p className="eyebrow">Subscription</p>
        {user?.entitled ? (
          <p className="f-serif text-3xl mt-3 flex items-center gap-3" data-testid="account-sub-status"><CheckCircle2 className="text-[var(--red)]" /> {user.role === "admin" ? "Editor access" : `${sub?.plan_name || "Active"} — active`}</p>
        ) : (
          <>
            <p className="f-serif text-3xl mt-3 flex items-center gap-3" data-testid="account-sub-status"><Lock size={24} /> No active plan</p>
            <p className="text-[var(--ink-2)] mt-2">Subscribe to unlock every peer-reviewed study and the full issues in the online reader.</p>
            <Link to="/subscribe" className="btn-red inline-flex h-11 px-6 items-center mt-6 font-semibold" data-testid="account-subscribe-btn">See plans</Link>
          </>
        )}
      </div>
      <button onClick={logout} className="mt-8 link-u text-sm" data-testid="account-logout-btn">Sign out</button>
      <MyLibrary />
    </div>
  );
}

export default function Account() {
  const { user, ready } = useAuth();
  return (
    <div className="px-5 lg:px-12 pt-14 grid lg:grid-cols-12 gap-14" data-testid="account-page">
      <div className="lg:col-span-5">
        <p className="eyebrow mb-6">Your BPJ</p>
        <h1 className="f-serif text-5xl sm:text-6xl tracking-tight leading-[0.95]"><MaskLines lines={["Reader", <em key="a" className="font-light">account.</em>]} /></h1>
        <p className="text-lg text-[var(--ink-2)] mt-8 max-w-sm leading-relaxed">One account for your subscription, the full research archive and every issue in the online reader.</p>
      </div>
      <div className="lg:col-span-6 lg:col-start-7">{ready && (user ? <Profile /> : <AuthForm />)}</div>
    </div>
  );
}
